import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState,
  getFacetedRowModel,
  getFacetedUniqueValues
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Divider,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import { useEmployeeColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useEffect, useMemo, useState } from 'react';
import { globalFilterFn } from '../../../util/filter';
import Pagination from '../shared/pagination';
import { observer } from 'mobx-react-lite';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { EmployeeOverviewToolBar } from './toolbars/employee-overview';
import { useActions } from '../../../../hooks/useActions';
import { Loading } from '../../atomic';
import { useNavigate } from 'react-router';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';

export const EmployeeTable = observer(() => {
  const { companyStore } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: employeesData = { pages: [], pageParams: [] },
    hasNextPage,
    fetchNextPage,
    isFetching,
    isLoading
  } = useListEmployees(companyStore.company?.id, {
    enabled: !!companyStore.company?.id
  });

  const flattenedData = useMemo(() => employeesData.pages.flat(), [employeesData]);

  useEffect(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [isBulkInviteProcessing, setIsBulkInviteProcessing] = useState(false);
  const { selectedEmployees, setSelectedEmployees } = useActions();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const columns = useEmployeeColumns();

  const onRowSelectionChange = (
    updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    const newValue =
      typeof updaterOrValue === 'function' ? updaterOrValue(selectedEmployees) : updaterOrValue;
    setSelectedEmployees(newValue);

    return newValue;
  };

  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    onColumnFiltersChange: setColumnFilters,
    sortDescFirst: false,
    state: {
      pagination,
      globalFilter,
      rowSelection: selectedEmployees,
      columnFilters
    },
    initialState: {
      columnVisibility: {
        location: false,
        department: false,
        cost_center: false,
        active_benefit: false,
        budget_exhausted: false,
        entry_date: false
      }
    }
  });

  if (isLoading) {
    return (
      <Paper
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Paper>
    );
  }

  return (
    <Paper className="relative overflow-y-hidden overflow-x-auto flex flex-col h-[calc(100vh-250px)]">
      {isBulkInviteProcessing && (
        <Box className="absolute inset-0 z-[1300] flex flex-col items-center justify-center gap-4 bg-white/75">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t('employee:BULK_INVITE_PROCESSING_HINT')}
          </Typography>
        </Box>
      )}
      <EmployeeOverviewToolBar
        table={table}
        onBulkInviteProcessingChange={setIsBulkInviteProcessing}
      />

      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 400px)')}>
        <Table size="small">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    isLoading={isFetching || isLoading}
                    hasData={flattenedData.length > 0}
                  />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  padding: 0,
                  borderBottom: 'none',
                  backgroundColor: 'transparent'
                }}
                colSpan={columns.length}
              >
                <DataTableLoadingBar isLoading={isFetching || isLoading} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isFetching && !isLoading && table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center">
                    <img
                      src={EmptyTable}
                      alt={t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                      className="w-64 h-64"
                    />
                    <p className={'mt-4 text-gray-500'}>
                      {t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      className="py-2 border-grey-100"
                      onDoubleClick={() => navigate(`/employees/${row.original.id}/dashboard`)}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider className="border-grey-100" />
      <Pagination table={table} />
    </Paper>
  );
});
